import { useCallback, useState } from "react";

import { ShiftPlannerShift } from "./shiftPlanner.types";

interface HistoryState {
	past: ShiftPlannerShift[][];
	present: ShiftPlannerShift[];
	future: ShiftPlannerShift[][];
}

export interface ShiftPlannerHistory {
	/** The current draft — pass straight to `ShiftPlanner`'s `shifts` prop. */
	shifts: ShiftPlannerShift[];
	/**
	 * Records one undoable step — pass straight to `ShiftPlanner`'s `onChange`.
	 * Any parent-side mutation (e.g. booking a consultant from the picker) must
	 * also go through this setter, or that change won't be undoable.
	 */
	setShifts: (shifts: ShiftPlannerShift[]) => void;
	undo: () => void;
	redo: () => void;
	canUndo: boolean;
	canRedo: boolean;
	/**
	 * Clears the undo/redo stacks — call after a successful save so steps that
	 * are already on the server can't be undone. Pass a value to also replace
	 * the draft (e.g. the server response after saving).
	 */
	reset: (shifts?: ShiftPlannerShift[]) => void;
}

/**
 * Undo/redo history for a `ShiftPlanner` draft. The component emits one
 * `onChange` per user gesture (a multi-slot "Klar", a batch delete), so each
 * history step matches what the user perceives as one action.
 *
 * `initialShifts` seeds the draft on mount only; use `reset(shifts)` to load
 * new server state later.
 */
export function useShiftPlannerHistory(
	initialShifts: ShiftPlannerShift[] = []
): ShiftPlannerHistory {
	const [history, setHistory] = useState<HistoryState>({
		past: [],
		present: initialShifts,
		future: [],
	});

	const setShifts = useCallback((shifts: ShiftPlannerShift[]) => {
		setHistory((h) => ({ past: [...h.past, h.present], present: shifts, future: [] }));
	}, []);

	const undo = useCallback(() => {
		setHistory((h) =>
			h.past.length === 0
				? h
				: {
						past: h.past.slice(0, -1),
						present: h.past[h.past.length - 1],
						future: [h.present, ...h.future],
				  }
		);
	}, []);

	const redo = useCallback(() => {
		setHistory((h) =>
			h.future.length === 0
				? h
				: {
						past: [...h.past, h.present],
						present: h.future[0],
						future: h.future.slice(1),
				  }
		);
	}, []);

	const reset = useCallback((shifts?: ShiftPlannerShift[]) => {
		setHistory((h) => ({ past: [], present: shifts ?? h.present, future: [] }));
	}, []);

	return {
		shifts: history.present,
		setShifts,
		undo,
		redo,
		reset,
		canUndo: history.past.length > 0,
		canRedo: history.future.length > 0,
	};
}
